import { useTranslation } from 'react-i18next';
import type { PresenceStatus } from '../api.ts';

/**
 * Il selettore di stato.
 *
 * "Il pulsante di stato è l'elemento più importante della console volontario"
 * (sezione 5.4): chi risponde anche al telefono fisso deve potersi marcare
 * occupato con un tap, altrimenti il sistema gli manda chiamate web mentre è
 * già impegnato.
 *
 * È un gruppo di radio vero, non tre bottoni che si somigliano: lo stato
 * corrente è annunciato dallo screen reader, si cambia con le frecce, e non è
 * mai dedotto dal solo colore (sezione 7bis).
 */

const CHOICES: { value: PresenceStatus; labelKey: string }[] = [
  { value: 'available', labelKey: 'console.presence_available' },
  { value: 'busy', labelKey: 'console.presence_busy' },
  { value: 'paused', labelKey: 'console.presence_paused' },
];

type Props = {
  value: PresenceStatus;
  disabled: boolean;
  onChange: (status: PresenceStatus) => void;
};

export function PresencePicker({ value, disabled, onChange }: Props) {
  const { t } = useTranslation();

  return (
    <fieldset className="presence" disabled={disabled}>
      <legend>{t('console.presence_label')}</legend>
      {/*
        La regola sotto l'etichetta, non un paragrafo come gli altri.
        Dice cosa cambia scegliendo — su "al telefono" e "in pausa" non
        arrivano chiamate — ed è l'unica riga che serve prima di toccare: con
        il corpo del testo normale competeva con le tre opzioni, che sono la
        cosa da guardare.
      */}
      <p className="presence__help" id="presence-help">
        {t('console.presence_help')}
      </p>

      {/*
        Niente `role="radiogroup"` qui: `fieldset` + `legend` + radio con lo
        stesso `name` producono già il raggruppamento, e un `radiogroup`
        annidato verrebbe annunciato senza nome — la `legend` nomina il
        `fieldset`, non lui.
      */}
      <div className="presence__options">
        {CHOICES.map((choice) => (
          <label
            key={choice.value}
            /*
              La classe dello stato c'è **sempre**, non solo quando è scelto:
              è lei a portare il colore, e `is-on` decide soltanto se quel
              colore si vede. Con le due cose in una classe sola i tre stati
              tornerebbero indistinguibili appena qualcuno semplifica.
            */
            className={`presence__option presence__option--${choice.value}${
              value === choice.value ? ' is-on' : ''
            }`}
          >
            <input
              type="radio"
              name="presence"
              value={choice.value}
              checked={value === choice.value}
              aria-describedby="presence-help"
              onChange={() => onChange(choice.value)}
            />
            <span>{t(choice.labelKey)}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
